<template>
  <div class="audience-create">
    <a class="back-link" href="#/audience/list">
      <i class="bi bi-arrow-left" aria-hidden="true"></i>
      Back to Instagram audiences
    </a>

    <header class="profile-hero">
      <div class="hero-left">
        <div class="hero-avatar">
          <img src="../../../assets/platforms/instagram.png" alt="Instagram"/>
        </div>
        <div class="hero-info">
          <span class="hero-platform">Instagram</span>
          <h1 class="hero-username">
            <a :href="'https://instagram.com/' + username" target="_blank">
              @{{ username }}
              <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
            </a>
          </h1>
          <div class="hero-meta">
            <span class="meta-chip">
              <span class="chip-label">Audience ID</span>
              <strong>{{ id > 0 ? '#' + id : '—' }}</strong>
            </span>
            <span class="meta-chip">
              <span class="chip-label">Instagram ID</span>
              <strong>{{ instagramId }}</strong>
            </span>
            <span class="meta-chip" v-if="id > 0">
              <span class="chip-label">Mode</span>
              <strong>{{ id > 0 ? 'Edit' : 'New' }}</strong>
            </span>
          </div>
        </div>
      </div>
    </header>

    <section class="collect-card">
      <div class="collect-info">
        <div class="collect-icon">
          <i class="bi bi-download" style="font-size:20px" aria-hidden="true"></i>
        </div>
        <div>
          <h2 class="collect-title">Collect audience data</h2>
          <p class="collect-desc">Pull demographics from the data provider for <strong>@{{ username }}</strong>. This populates the form below — you can still edit values before saving.</p>
        </div>
      </div>
      <button class="btn btn-collect" :disabled="loader.data" @click="getAudienceData()">
        <span v-if="!loader.data" class="btn-content">
          <i class="bi bi-cloud-arrow-down" aria-hidden="true"></i>
          Fetch from API
        </span>
        <span v-else class="btn-content">
          <span class="spinner"></span>
          Fetching…
        </span>
      </button>
    </section>

    <section class="form-card">
      <audience-form
        :loader="loader"
        :audiences="audiences"
        @deleteAudienceDetail="deleteAudienceDetail"
        :module="['quality_distribution', 'age', 'lang', 'gender', 'followers', 'city']"
      ></audience-form>
    </section>

    <div class="action-bar">
      <div class="action-bar-inner">
        <div class="action-bar-info">
          <span class="dot dot-ok"></span>
          <span v-if="id > 0">Editing audience <strong>#{{ id }}</strong> for <strong>@{{ username }}</strong></span>
          <span v-else>New audience for <strong>@{{ username }}</strong></span>
        </div>
        <div class="action-bar-buttons">
          <a class="btn btn-outline-secondary" href="#/audience/list">Cancel</a>
          <button class="btn btn-primary btn-save" :disabled="loader.save" @click="addAudience()">
            <span v-if="!loader.save" class="btn-content">
              {{ id > 0 ? 'Save audience' : 'Add audience' }}
            </span>
            <span v-else class="btn-content">
              <span class="spinner"></span> Saving…
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AudienceForm from "../AudienceForm.vue";

export default {
  name: "audience-create-instagram",
  components: {AudienceForm},
  data() {
    return {
      apiHost: this.$appSettings.getSetting('host'),
      id: 0,
      instagramId: this.$route.params.instagramId,
      username: this.$route.params.username,
      loader: {
        data: false,
        save: false
      },
      audiences: {
        followers: this.$audience.audienceFollowers,
        quality_distribution: this.$audience.audienceQualityDistribution,
        city: [{ id: 0, k: '', v: '' }],
        lang: [{ id: 0, k: '', v: '' }],
        age: this.$audience.audienceAge,
        gender: this.$audience.audienceGender
      },
      audience: {},
      status: 0
    }
  },
  methods: {
    getAudience() {
      const params = { token: this.$auth.getToken() };
      this.$http.get(this.apiHost + 'audience/detail/' + this.instagramId, { params: params }).then(response => {
        const data = response.body;
        if (data.id !== undefined) {
          this.cleanAudiences();
          this.id = data.id;
          this.instagramId = data.instagram_id;
          this.username = data.username;
          this.status = data.status;
          if (data.audience.age.length > 0) {
            this.audiences = data.audience;
          }
          this.audiences.gender = data.audience.gender.map(v => ({ ...v, k: parseInt(v.k) }));
        }
      })
    },
    prepareAudienceData(data) {
      const audience = data.data.data.prepared_data;
      const preparedAudience = this.$audience.getPreparedAudience(audience);
      this.audiences.followers = preparedAudience.followers;
      this.audiences.quality_distribution = preparedAudience.quality_distribution;
      this.audiences.city = preparedAudience.city;
      this.audiences.lang = preparedAudience.lang;
      this.audiences.age = preparedAudience.age;
      this.audiences.gender = preparedAudience.gender;
    },
    getAudienceData() {
      this.loader.data = true;
      const params = { token: this.$auth.getToken() };
      this.$http.get(this.apiHost + 'audience/data/' + this.instagramId, { params: params }).then(response => {
        this.loader.data = false;
        const data = response.body;
        if (data.data.data.code === 200) {
          this.prepareAudienceData(data);
          this.$notify({ group: 'default', type: 'success', text: 'Audience data has been collected' });
        } else {
          this.$notify({ group: 'default', type: 'error', text: 'Error while downloading audience data' });
        }
      }, () => {
        this.loader.data = false;
        this.$notify({ group: 'default', type: 'error', text: 'Request failed' });
      })
    },
    addAudience() {
      if (!this.validate()) return;
      this.loader.save = true;
      const body = {
        instagram_id: this.instagramId,
        username: this.username,
        status: 1,
        audience: this.audiences
      };
      this.$http.post(this.apiHost + 'audience/', body, {
        params: { token: this.$auth.getToken() }
      }).then(() => {
        this.loader.save = false;
        const notifyText = this.id > 0 ? 'Audience has been updated' : 'Audience has been added';
        this.$notify({ group: 'default', type: 'success', text: notifyText });
      }, () => {
        this.loader.save = false;
        this.$notify({ group: 'default', type: 'error', text: 'Save failed' });
      })
    },
    deleteAudienceDetail(id, $idx, type) {
      this.$http.delete(this.apiHost + 'audience/detail/' + id, {
        params: { token: this.$auth.getToken() }
      }).then(() => {
        this.audiences[type].splice($idx, 1);
        this.$notify({ group: 'default', type: 'success', text: 'Audience ' + type + ' has been deleted' });
      })
    },
    cleanAudiences() {
      this.audiences.lang = [];
      this.audiences.city = [];
      this.audiences.age = [];
      this.audiences.quality_distribution = [];
      this.audiences.followers = [];
      this.audiences.gender = [];
    },
    validate() {
      if (this.audiences.city.length === 0) return this.failValidation('Please add at least one city');
      if (this.audiences.lang.length === 0) return this.failValidation('Please add at least one language');
      if (this.countSummaryPercent('city') !== 100) return this.failValidation('City percentage must total 100%');
      if (this.countSummaryPercent('lang') !== 100) return this.failValidation('Language percentage must total 100%');
      if (this.countSummaryPercent('age') !== 100) return this.failValidation('Age percentage must total 100%');
      if (this.countSummaryPercent('quality_distribution') !== 100) return this.failValidation('Quality percentage must total 100%');
      if (this.countSummaryPercent('followers') !== 100) return this.failValidation('Followers percentage must total 100%');
      if (this.countSummaryPercent('gender') !== 100) return this.failValidation('Gender percentage must total 100%');
      return true;
    },
    failValidation(text) {
      this.$notify({ group: 'default', type: 'error', text: text });
      return false;
    },
    countSummaryPercent(type) {
      let sum = 0;
      for (let i = 0; i < this.audiences[type].length; i++) {
        if (this.audiences[type][i].v === '') continue;
        sum += parseFloat(this.audiences[type][i].v);
      }
      return Math.round(sum);
    }
  },
  created: function () {
    this.getAudience();
  }
}
</script>

<style scoped>
  .audience-create {
    max-width: 1080px;
    margin: 0 auto 160px;
    color: #1f2937;
  }
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    margin-bottom: 14px;
  }
  .back-link:hover { color: #265a88; }

  .profile-hero {
    padding: 24px;
    background: linear-gradient(135deg, #fdf4ff 0%, #fff7ed 50%, #fff 100%);
    border: 1px solid #f5d0fe;
    border-radius: 16px;
    margin-bottom: 18px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .hero-left {
    display: flex;
    gap: 18px;
    align-items: center;
  }
  .hero-avatar {
    width: 72px;
    height: 72px;
    border-radius: 18px;
    background: linear-gradient(135deg, #f9ce34 0%, #ee2a7b 50%, #6228d7 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 18px rgba(225, 48, 108, 0.3);
    flex-shrink: 0;
  }
  .hero-avatar img {
    width: 40px;
    height: 40px;
    filter: brightness(0) invert(1);
  }
  .hero-info { min-width: 0; flex: 1; }
  .hero-platform {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #ee2a7b;
    margin-bottom: 4px;
  }
  .hero-username {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 10px;
    letter-spacing: -0.02em;
    color: #0f172a;
    line-height: 1.1;
  }
  .hero-username a {
    color: inherit;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .hero-username a .bi { color: #6b7280; }
  .hero-username a:hover { color: #265a88; }
  .hero-username a:hover .bi { color: #265a88; }
  .hero-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 4px 12px 4px 10px;
    font-size: 12.5px;
  }
  .chip-label {
    color: #6b7280;
    text-transform: uppercase;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
  }
  .meta-chip strong {
    color: #0f172a;
    font-weight: 600;
  }

  .collect-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px 20px;
    margin-bottom: 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .collect-info {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
  }
  .collect-icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #eaf1f9;
    color: #265a88;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .collect-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 2px;
    color: #0f172a;
  }
  .collect-desc {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
  }
  .collect-desc strong { color: #0f172a; }
  .btn-collect {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    padding: 10px 18px;
    background: #265a88;
    color: #fff;
    border: 1px solid #265a88;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(38, 90, 136, 0.2);
    transition: background 0.15s, box-shadow 0.15s;
  }
  .btn-collect:hover:not(:disabled) {
    background: #1f4a70;
    box-shadow: 0 4px 12px rgba(38, 90, 136, 0.3);
  }
  .btn-collect:disabled { opacity: 0.65; cursor: not-allowed; }
  .btn-content { display: inline-flex; align-items: center; gap: 6px; }

  .form-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 4px 8px 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .action-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: saturate(180%) blur(12px);
    -webkit-backdrop-filter: saturate(180%) blur(12px);
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 -4px 20px rgba(15, 23, 42, 0.06);
    z-index: 50;
  }
  .action-bar-inner {
    max-width: 1080px;
    margin: 0 auto;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
  }
  .action-bar-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6b7280;
  }
  .action-bar-info strong { color: #0f172a; font-weight: 600; }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }
  .dot-ok {
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  }
  .action-bar-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .btn-save {
    display: inline-flex;
    align-items: center;
    padding: 9px 18px;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(38, 90, 136, 0.2);
  }
  .btn-save:disabled { opacity: 0.65; cursor: not-allowed; }

  .spinner {
    display: inline-block;
    width: 13px;
    height: 13px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    .collect-card {
      flex-direction: column;
      align-items: stretch;
    }
    .btn-collect { justify-content: center; }
    .action-bar-inner {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      padding: 12px 16px;
    }
    .action-bar-buttons { justify-content: stretch; }
    .action-bar-buttons .btn { flex: 1; justify-content: center; }
    .hero-username { font-size: 22px; }
    .hero-avatar { width: 56px; height: 56px; }
    .hero-avatar img { width: 30px; height: 30px; }
  }
</style>



// WEBPACK FOOTER //
// src/components/audience/instagram/AudienceCreate.vue